
import React, { useState, useEffect } from 'react';
import { AIEvent, Comment, User, Theme, AppConfig } from '../types';
import { storageService } from '../services/storageService';
import { supabaseService } from '../services/supabaseService';
import { CATEGORIES, PRIORITY_LEVELS } from '../constants';
import CommentSection from './CommentSection';

interface DetailDrawerProps {
  event: AIEvent;
  onClose: () => void;
  currentUser: User;
  theme: Theme;
  onUpdateEvent: (event: AIEvent) => Promise<void>;
  config?: AppConfig; // Make config optional for backward compatibility types, but mostly required now
}

const DetailDrawer: React.FC<DetailDrawerProps> = ({ event, onClose, currentUser, theme, onUpdateEvent, config }) => {
  const [comments, setComments] = useState<Comment[]>([]);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [loadingComments, setLoadingComments] = useState(false);
  const [editForm, setEditForm] = useState<AIEvent>(event);

  // Fetch comments (Cloud or Local)
  useEffect(() => {
    const fetchComments = async () => {
      setLoadingComments(true);
      try {
        if (config && supabaseService.isConfigured(config) && config.supabaseCommentsTableName) {
          const remoteComments = await supabaseService.fetchComments(config, event.id);
          setComments(remoteComments);
        } else {
          setComments(storageService.getComments(event.id));
        }
      } catch (e) {
        console.error("Error loading comments", e);
        // Fallback or just empty
      } finally {
        setLoadingComments(false);
      }
    };

    fetchComments();
    setEditForm(event);
    setIsEditing(false);
  }, [event, config]);

  const handleAddComment = async (text: string) => {
    const newComment: Comment = { 
      id: Date.now().toString(), 
      eventId: event.id, 
      authorEmail: currentUser.email, 
      text, 
      timestamp: new Date().toISOString() 
    };

    try {
      if (config && supabaseService.isConfigured(config) && config.supabaseCommentsTableName) {
         // Optimistic Update
         setComments(prev => [...prev, newComment]);
         await supabaseService.insertComment(config, newComment);
      } else {
         storageService.addComment(newComment);
         setComments(prev => [...prev, newComment]);
      }
    } catch (e) {
      console.error("Failed to post comment", e);
      alert("Failed to save comment to cloud.");
      // Rollback optimistic update if needed, but simple alert for now
    }
  };

  const handleStartDateChange = (dateStr: string) => {
    const date = new Date(dateStr);
    if (!isNaN(date.getTime())) {
      setEditForm(prev => ({
        ...prev,
        startDate: dateStr,
        year: date.getFullYear(),
        month: date.getMonth() + 1
      }));
    } else {
      setEditForm(prev => ({ ...prev, startDate: dateStr }));
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try { 
      await onUpdateEvent(editForm); 
      setIsEditing(false); 
    } catch (err) {
      console.error("Failed to update event:", err);
    } finally { 
      setIsSaving(false); 
    }
  };

  const isLight = theme === 'light';

  const inputClassName = `w-full text-xs p-3 rounded-xl border bg-transparent focus:ring-2 focus:ring-accent/10 transition-all ${
    isLight ? 'border-slate-200 text-slate-900 bg-white placeholder:text-slate-300' : 'border-white/20 text-white bg-[#1a1a1a] placeholder:text-white/10'
  } disabled:opacity-60 disabled:cursor-not-allowed`;

  const labelClassName = `text-[10px] font-bold uppercase ml-1 block mb-1.5 tracking-wider ${isLight ? 'text-slate-400' : 'text-white/20'}`;

  const optionClassName = isLight ? 'bg-white text-slate-900' : 'bg-[#1a1a1a] text-white';

  return (
    <>
      <div 
        className="fixed inset-0 z-40 bg-slate-900/40 backdrop-blur-[1px] animate-in fade-in duration-300" 
        onClick={onClose}
        aria-hidden="true"
      />

      <div 
        className={`fixed inset-y-0 right-0 w-[550px] shadow-2xl z-50 flex flex-col transform transition-all animate-in slide-in-from-right duration-300 border-l ${
          isLight ? 'bg-white border-slate-200' : 'bg-[#0a0a0a] border-white/10'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={`p-6 border-b flex items-center justify-between ${isLight ? 'bg-slate-50 border-slate-100' : 'bg-black/40 border-white/5'}`}>
          <div className="flex-1 mr-4 overflow-hidden">
            {isEditing ? (
              <input 
                className={`${inputClassName} font-bold text-base py-2`}
                value={editForm.campaignName}
                onChange={(e) => setEditForm({...editForm, campaignName: e.target.value})}
                placeholder="Campaign Name"
              />
            ) : (
              <h2 className={`text-lg font-bold truncate ${isLight ? 'text-slate-900' : 'text-white'}`}>{event.campaignName}</h2>
            )}
          </div>
          
          <div className="flex items-center gap-2">
            {!isEditing ? (
              <button 
                onClick={() => setIsEditing(true)} 
                className="p-2 text-slate-400 hover:text-accent transition-colors"
                title="Edit record"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-5M16.5 3.5a2.121 2.121 0 013 3L7 19l-4 1 1-4L16.5 3.5z" />
                </svg>
              </button>
            ) : (
              <button 
                onClick={handleSave} 
                disabled={isSaving} 
                className="px-4 py-2 bg-slate-900 text-white dark:bg-accent dark:text-black text-[11px] font-bold rounded-lg shadow-lg hover:opacity-90 active:scale-95 transition-all flex items-center gap-2 uppercase tracking-widest"
              >
                {isSaving ? (
                  <svg className="animate-spin h-3 w-3" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                ) : null}
                {isSaving ? 'Processing' : 'Commit Changes'}
              </button>
            )}
            <button 
              onClick={onClose} 
              className="p-2 text-slate-400 hover:text-red-500 transition-colors"
              title="Close panel"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-8 space-y-10 pb-32">
          {/* Identity & Reference */}
          <section className="space-y-4">
            <h3 className={`text-[10px] uppercase font-bold tracking-widest ${isLight ? 'text-slate-400' : 'text-white/20'}`}>Intel Foundation</h3>
            <div className="space-y-4">
              <div className="space-y-1">
                <label className={labelClassName}>OFFICIAL URL</label>
                <input 
                  type="text"
                  className={inputClassName}
                  value={editForm.url}
                  onChange={(e) => setEditForm({...editForm, url: e.target.value})}
                  disabled={!isEditing}
                  placeholder="https://..."
                />
              </div>
              <div className="space-y-1">
                <label className={labelClassName}>EVENT FOCUS</label>
                <input 
                  className={inputClassName}
                  value={editForm.focusArea}
                  onChange={(e) => setEditForm({...editForm, focusArea: e.target.value})}
                  disabled={!isEditing}
                  placeholder="Concise focus summary"
                />
              </div>
            </div>
          </section>

          {/* Classification */}
          <section className="space-y-4">
            <h3 className={`text-[10px] uppercase font-bold tracking-widest ${isLight ? 'text-slate-400' : 'text-white/20'}`}>Taxonomy</h3>
            <div className="grid grid-cols-2 gap-6">
              <div className="space-y-1">
                <label className={labelClassName}>SECTOR CATEGORY</label>
                <select 
                  className={inputClassName} 
                  value={editForm.category} 
                  onChange={(e) => setEditForm({...editForm, category: e.target.value})} 
                  disabled={!isEditing}
                >
                  {CATEGORIES.map(c => <option key={c} value={c} className={optionClassName}>{c}</option>)}
                </select>
              </div>
              <div className="space-y-1">
                <label className={labelClassName}>STRATEGIC PRIORITY</label>
                <select 
                  className={inputClassName} 
                  value={editForm.priority} 
                  onChange={(e) => setEditForm({...editForm, priority: e.target.value})} 
                  disabled={!isEditing}
                >
                  <option value="" className={optionClassName}>Unranked</option>
                  {PRIORITY_LEVELS.map(p => <option key={p} value={p} className={optionClassName}>{p}</option>)}
                </select>
              </div>
            </div>
          </section>

          {/* Demographics & Scope */}
          <section className="space-y-4">
            <h3 className={`text-[10px] uppercase font-bold tracking-widest ${isLight ? 'text-slate-400' : 'text-white/20'}`}>Demographics & Scope</h3>
            <div className="grid grid-cols-2 gap-6">
              <div className="space-y-1">
                <label className={labelClassName}>AUDIENCE SIZE</label>
                <input 
                  className={inputClassName}
                  value={editForm.attendeeSize || ''}
                  onChange={(e) => setEditForm({...editForm, attendeeSize: e.target.value})}
                  disabled={!isEditing}
                  placeholder="e.g. 5000+"
                />
              </div>
              <div className="space-y-1">
                <label className={labelClassName}>TARGET AUDIENCE</label>
                <input 
                  className={inputClassName}
                  value={editForm.targetAudience || ''}
                  onChange={(e) => setEditForm({...editForm, targetAudience: e.target.value})}
                  disabled={!isEditing}
                  placeholder="e.g. Developers, Executives"
                />
              </div>
            </div>
          </section>

          {/* Schedule & Location */}
          <section className="space-y-4">
            <h3 className={`text-[10px] uppercase font-bold tracking-widest ${isLight ? 'text-slate-400' : 'text-white/20'}`}>Chronology & Logistics</h3>
            <div className="grid grid-cols-2 gap-6">
              <div className="space-y-1">
                <label className={labelClassName}>START DATE</label>
                <input 
                  type="date"
                  className={inputClassName}
                  value={editForm.startDate}
                  onChange={(e) => handleStartDateChange(e.target.value)}
                  disabled={!isEditing}
                />
              </div>
              <div className="space-y-1">
                <label className={labelClassName}>END DATE</label>
                <input 
                  type="date"
                  className={inputClassName}
                  value={editForm.endDate}
                  onChange={(e) => setEditForm({...editForm, endDate: e.target.value})}
                  disabled={!isEditing}
                />
              </div>
              <div className="md:col-span-2 space-y-1">
                <label className={labelClassName}>LOCATION</label>
                <input 
                  className={inputClassName}
                  value={editForm.location}
                  onChange={(e) => setEditForm({...editForm, location: e.target.value})}
                  disabled={!isEditing}
                  placeholder="City, Country or Distributed"
                />
              </div>
            </div>
          </section>

          {/* Detailed Intel */}
          <section className="space-y-3">
            <h3 className={`text-[10px] uppercase font-bold tracking-widest ${isLight ? 'text-slate-400' : 'text-white/20'}`}>Technical Intel Brief</h3>
            <textarea 
              className={`${inputClassName} h-40 resize-none leading-relaxed`} 
              value={editForm.information} 
              onChange={(e) => setEditForm({...editForm, information: e.target.value})} 
              disabled={!isEditing}
              placeholder="Intelligence overview..."
            />
          </section>

          <div className={`pt-8 border-t ${isLight ? 'border-slate-100' : 'border-white/5'}`}>
            {loadingComments ? (
              <div className="flex justify-center py-8">
                 <div className="w-5 h-5 border-2 border-accent/30 border-t-accent rounded-full animate-spin"></div>
              </div>
            ) : (
              <CommentSection 
                comments={comments} 
                onAddComment={handleAddComment} 
                currentUser={currentUser} 
                theme={theme} 
              />
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default DetailDrawer;